<template>
  <div class="map-container" ref="mapContainer">
    <!-- 地图容器 -->
    <div id="map" class="map-content"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'

// 定义组件名称
defineOptions({
  name: 'MapComponent'
})

// 定义props
const props = defineProps({
  // 天地图API密钥
  tdtKey: {
    type: String,
    required: true,
    default: ''
  },
  // 地图中心点坐标 [经度, 纬度]
  center: {
    type: Array,
    default: () => [116.397428, 39.90923] // 默认北京
  },
  // 地图缩放级别
  zoom: {
    type: Number,
    default: 12
  },
  // 地图类型：'vec'矢量, 'img'影像, 'ter'地形
  mapType: {
    type: String,
    default: 'vec',
    validator: (value) => ['vec', 'img', 'ter'].includes(value)
  }
})

// 定义emits
const emit = defineEmits(['map-ready'])

// 响应式数据
const mapContainer = ref(null)
const map = ref(null) // 地图实例

// 加载天地图API
const loadTdtApi = () => {
  return new Promise((resolve, reject) => {
    // 检查是否已经加载
    if (window.T) {
      resolve()
      return
    }
    // 创建script标签加载天地图API
    const script = document.createElement('script')
    script.src = `http://api.tianditu.gov.cn/api?v=4.0&tk=${props.tdtKey}`
    script.type = 'text/javascript'
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('天地图API加载失败'))
    document.head.appendChild(script)
  })
}

// 创建地图
const createMap = () => {
  try {
    // 创建地图实例
    map.value = new T.Map('map')

    // 设置地图中心点和缩放级别
    map.value.centerAndZoom(new T.LngLat(props.center[0], props.center[1]), props.zoom)

    // 触发地图初始化完成事件
    emit('map-ready', map.value)

  } catch (error) {
    console.error('创建地图失败:', error)
  }
}

// 销毁地图
const destroyMap = () => {
  if (map.value) {
    map.value.clearOverLays()
    map.value = null
  }
}

// 初始化地图
const initMap = () => {
  if (!props.tdtKey) {
    console.error('天地图API密钥不能为空')
    return
  }

  // 动态加载天地图API
  loadTdtApi().then(() => {
    createMap()
  }).catch(error => {
    console.error('加载天地图API失败:', error)
  })
}

// 监听属性变化
watch(() => props.center, (newCenter) => {
  if (map.value && newCenter) {
    map.value.centerAndZoom(new T.LngLat(newCenter[0], newCenter[1]), props.zoom)
  }
}, { deep: true })

watch(() => props.zoom, (newZoom) => {
  if (map.value) {
    map.value.setZoom(newZoom)
  }
})

watch(() => props.mapType, (newType) => {
  if (map.value) {
    addLayer(newType)
  }
})

// 生命周期钩子
onMounted(() => {
  nextTick(() => {
    initMap()
  })
})

onBeforeUnmount(() => {
  destroyMap()
})
</script>

<style scoped>
.map-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.map-content {
  width: 100%;
  height: 100%;
  min-height: 400px;
}
</style>